import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { X, Send } from 'lucide-react';
import { submitForm, fetchFormStatus, fetchFormSubmission } from '../../../services/employeeFormsApi';
import { formatDate as formatDateUtil } from '../../../utils/dateUtils';

export default function MarriageDeclarationPreview() {
  const navigate = useNavigate();
  const { formCode } = useParams();
  const [formData, setFormData] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [formStatus, setFormStatus] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const loadFormData = async () => {
      try {
        const code = formCode || 'MARR_DECL';
        setLoading(true);
        setError(null);

        const submission = await fetchFormSubmission(code);
        
        if (submission && submission.form_data) {
          setFormData(submission.form_data);
          setFormStatus({
            status: submission.status,
            submitted_at: submission.submitted_at,
            updated_at: submission.updated_at
          });
        } else {
          setError('No draft or submitted form found.');
        }
      } catch (err) {
        console.error('Error loading form data:', err);
        setError('Failed to load form data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadFormData();
  }, [formCode]);

  const handleClose = () => {
    navigate(-1);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const code = formCode || 'MARR_DECL';
      await submitForm(code, formData);
      alert('Form submitted successfully!');
      navigate('/user-dashboard?tab=my-forms', { replace: true });
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to submit form: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = () => {
    if (!formStatus) return true;
    return formStatus.status === 'DRAFT' || formStatus.status === 'REJECTED';
  };

  const getStatusDisplay = (status) => {
    const statusMap = {
      'DRAFT': { text: 'Draft', color: 'bg-gray-100 text-gray-800' },
      'SUBMITTED': { text: 'Submitted', color: 'bg-blue-100 text-blue-800' },
      'APPROVED': { text: 'Approved', color: 'bg-green-100 text-green-800' },
      'REJECTED': { text: 'Rejected', color: 'bg-red-100 text-red-800' }
    };
    return statusMap[status] || { text: status, color: 'bg-gray-100 text-gray-800' };
  };

  const formatDate = (dateStr) => {
    return formatDateUtil(dateStr, '________________________');
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Loading preview...</div>
        </div>
      </div>
    );
  }

  if (error || !formData) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-red-600">{error || 'No form data found'}</div>
        </div>
      </div>
    );
  }

  const statusDisplay = getStatusDisplay(formStatus?.status);

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Marriage Declaration Form</h1>
          <div className="flex items-center gap-3">
            {formStatus && (
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusDisplay.color}`}>
                {statusDisplay.text}
              </span>
            )}
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Government Form Preview */}
        <div className="bg-white border-2 border-gray-800 p-8 shadow-lg">
          {/* Form Title */}
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              DECISION NO. 2 UNDER RULE 21 OF CENTRAL CIVIL SERVICES (CONDUCT) RULES, 1964
            </h2>
            <p className="text-gray-700">Marriage Declaration Form</p>
          </div>

          {/* Employee Details Section */}
          <div className="mb-6">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-semibold text-gray-800">Name of the Government Servant :</span>
                <div className="mt-1 border-b border-gray-800 pb-1">
                  <span className="text-gray-900">{formData.employeeName || '________________________'}</span>
                </div>
              </div>
              <div>
                <span className="font-semibold text-gray-800">Employee Code :</span>
                <div className="mt-1 border-b border-gray-800 pb-1">
                  <span className="text-gray-900">{formData.employeeCode || '________________________'}</span>
                </div>
              </div>
              <div>
                <span className="font-semibold text-gray-800">Department :</span>
                <div className="mt-1 border-b border-gray-800 pb-1">
                  <span className="text-gray-900">{formData.department || '________________________'}</span>
                </div>
              </div>
              <div>
                <span className="font-semibold text-gray-800">Designation :</span>
                <div className="mt-1 border-b border-gray-800 pb-1">
                  <span className="text-gray-900">{formData.designation || '________________________'}</span>
                </div>
              </div>
              <div>
                <span className="font-semibold text-gray-800">Date of Birth :</span>
                <div className="mt-1 border-b border-gray-800 pb-1">
                  <span className="text-gray-900">{formatDate(formData.dateOfBirth)}</span>
                </div>
              </div>
              <div>
                <span className="font-semibold text-gray-800">Date of Appointment :</span>
                <div className="mt-1 border-b border-gray-800 pb-1">
                  <span className="text-gray-900">{formatDate(formData.dateOfAppointment)}</span>
                </div>
              </div>
              <div>
                <span className="font-semibold text-gray-800">Date of Retirement :</span>
                <div className="mt-1 border-b border-gray-800 pb-1">
                  <span className="text-gray-900">{formatDate(formData.dateOfRetirement)}</span>
                </div>
              </div>
              <div className="col-span-2">
                <span className="font-semibold text-gray-800">Permanent Address :</span>
                <div className="mt-1 border-b border-gray-800 pb-1">
                  <span className="text-gray-900 whitespace-pre-line">{formData.permanentAddress || '________________________'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Marital Status Section */}
          <div className="mb-6">
            <h3 className="font-bold text-gray-900 mb-4">Marital Status Declaration</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <span className="text-gray-800">I declare that I am:</span>
              </div>
              <div className="pl-4 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 border border-gray-800 inline-block"></span>
                  <span className="text-gray-900">Unmarried</span>
                  {formData.maritalStatus === 'unmarried' && <span className="text-gray-900 font-bold">✓</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 border border-gray-800 inline-block"></span>
                  <span className="text-gray-900">Widower</span>
                  {formData.maritalStatus === 'widower' && <span className="text-gray-900 font-bold">✓</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 border border-gray-800 inline-block"></span>
                  <span className="text-gray-900">Widow</span>
                  {formData.maritalStatus === 'widow' && <span className="text-gray-900 font-bold">✓</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 border border-gray-800 inline-block"></span>
                  <span className="text-gray-900">Married and only one spouse living</span>
                  {formData.maritalStatus === 'married_one_spouse' && <span className="text-gray-900 font-bold">✓</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 border border-gray-800 inline-block"></span>
                  <span className="text-gray-900">Married while spouse living</span>
                  {formData.maritalStatus === 'married_while_spouse_living' && <span className="text-gray-900 font-bold">✓</span>}
                </div>
              </div>

              {formData.maritalStatus === 'married_while_spouse_living' && (
                <div className="mt-4 pl-4">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-800">Application for exemption enclosed:</span>
                    <span className="font-semibold text-gray-900">{formData.exemptionEnclosed === 'yes' ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Declaration Section */}
          <div className="mb-6">
            <div className="text-sm text-gray-800 mb-4">
              <p className="mb-2">
                I hereby declare that the information provided above is true and correct to the best of my knowledge and belief.
              </p>
              <p>
                I understand that any false statement may lead to disciplinary action under the Central Civil Services (Conduct) Rules, 1964.
              </p>
            </div>
          </div>

          {/* Signature Section */}
          <div className="mb-6">
            <div className="grid grid-cols-2 gap-8 text-sm">
              <div>
                <span className="font-semibold text-gray-800">Place:</span>
                <div className="mt-1 border-b border-gray-800 pb-1">
                  <span className="text-gray-900">{formData.place || '________________________'}</span>
                </div>
              </div>
              <div>
                <span className="font-semibold text-gray-800">Date:</span>
                <div className="mt-1 border-b border-gray-800 pb-1">
                  <span className="text-gray-900">{formatDate(formData.declarationDate)}</span>
                </div>
              </div>
            </div>
            <div className="mt-8">
              <div className="border-b border-gray-800 pb-1 w-64">
                <span className="text-gray-900">Signature of Government Servant</span>
              </div>
            </div>
          </div>

          {/* Note Section */}
          <div className="mt-8 pt-4 border-t border-gray-300">
            <p className="text-xs text-gray-600 italic">
              Note: This declaration is required under Rule 21 of Central Civil Services (Conduct) Rules, 1964. 
              Any false declaration may result in disciplinary action.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 mt-6">
          {canSubmit() && (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
              {submitting ? 'Submitting...' : 'Submit Form'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
